import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from "../components/Navbar";

export function CreateFeedback() {
  const [projectName, setProjectName] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectName) return alert('Project name is required.');
    setLoading(true);
    try {
      const urls = [];
      for (const file of files) {
        const storageRef = ref(storage, `feedback-images/${projectName}-${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        urls.push(url);
      }

      await setDoc(doc(db, 'feedbacks', projectName), {
        projectName,
        images: urls,
        createdAt: new Date().toISOString()
      }, { merge: true });

      const link = `https://lab-developers.web.app/submit-feedback/${projectName}`;
      setShareLink(link);
    } catch (error) {
      console.error(error);
      alert('Error creating feedback entry.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -mr-48 -mt-48 animate-blob" />
      <div className="fixed bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full -ml-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      <div className="container mx-auto max-w-2xl px-6 py-64 relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-32"
        >
          <div className="absolute -inset-14 bg-blue-500/10 blur-[130px] rounded-full pointer-events-none" />
          <h1 className="text-7xl md:text-[8rem] font-black text-white mb-10 tracking-[0.02em] leading-[0.85] relative z-10 uppercase font-inter">
            Admin <br /><span className="text-gradient from-blue-600 via-slate-600 to-indigo-600">Protocol.</span>
          </h1>
          <p className="text-xl md:text-3xl text-slate-400 max-w-4xl mx-auto font-medium lowercase tracking-tight relative z-10">
            initialize new project verification nodes.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="group glass-card p-16 md:p-20 rounded-[5rem] border border-white/80 shadow-2xl shadow-slate-200/50 text-left relative overflow-hidden"
        >
          <form onSubmit={handleSubmit} className="space-y-12">
            <div className="space-y-4">
              <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-8">Project Identifier</label>
              <input
                name="projectName"
                placeholder="PROJ-789-ALPHA"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
                className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-10 py-7 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all font-black uppercase tracking-widest text-sm placeholder:text-slate-200"
              />
            </div>

            <div className="space-y-4">
              <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-8">Visual Documentation</label>
              <div className="relative">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-10 py-7 text-slate-400 focus:outline-none focus:border-blue-500 transition-all font-medium file:mr-6 file:py-2.5 file:px-8 file:rounded-full file:border-0 file:text-[10px] file:font-black file:bg-blue-600 file:text-white file:uppercase file:tracking-widest cursor-pointer"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full py-7 bg-slate-900 text-white font-black rounded-[2.5rem] overflow-hidden transition-all shadow-2xl active:scale-95 disabled:opacity-50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="relative z-10 flex items-center justify-center gap-4 uppercase tracking-[0.2em] text-[11px]">
                {loading ? 'SYNCHRONIZING...' : 'INITIALIZE ENTRY'}
                <span className="text-xl transition-transform group-hover:translate-x-1">→</span>
              </span>
            </button>

            <AnimatePresence>
              {shareLink && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ height: 0 }}
                  className="mt-16 pt-16 border-t border-white/5 space-y-10"
                >
                  <div className="text-center">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.5em] block mb-8">Client Access Protocol</label>
                    <div className="bg-[#020617] border border-white/5 p-8 rounded-[3rem] text-[10px] font-black text-blue-600 break-all text-center tracking-[0.2em] group-hover:bg-slate-900/40/50 transition-colors">
                      {shareLink}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="w-full py-6 bg-slate-900/40 border border-white/10 text-white font-black rounded-[2.5rem] hover:bg-[#020617] transition-all uppercase tracking-[0.3em] text-[11px] shadow-sm hover:shadow-xl hover:shadow-slate-100/50"
                  >
                    COPY ACCESS LINK
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.div>
      </div>

      <footer className="py-24 px-6 bg-slate-900/40 border-t border-white/5 text-center">
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.5em]">© 2024 LAB Developers — Internal Access Only.</p>
      </footer>
    </div>
  );
}
